<template>
    <div>
      <Header />
  
      <!-- ── Back + breadcrumb ── -->
      <div class="back-row">
        <button class="btn-back" @click="$router.back()">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
      <div class="page-header">
        <div>
          <h5 class="page-title">Employee Details</h5>
          <p class="page-sub">
            <router-link :to="$url('/payroll/employees')" class="breadcrumb-link">Employees</router-link>
            <span class="breadcrumb-sep">/</span>
            <span>Employee Details</span>
          </p>
        </div>
        <div class="page-actions">
          <button class="btn-outline" @click="statusOpen = true">
            <font-awesome-icon icon="exchange-alt" /> Update Status
          </button>
        </div>
      </div>
  
      <!-- ── Loading ── -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading employee...</p>
      </div>
  
      <template v-else-if="employee">
  
        <!-- ══════════════════════════════
             PERSONAL INFO
        ══════════════════════════════ -->
        <div class="detail-card mb-5">
          <div class="detail-card-hd">
            <div class="detail-card-hd-left">
              <!-- Avatar -->
              <div class="emp-avatar-wrap">
                <img v-if="employee.avatar_url && !avatarError" :src="employee.avatar_url"
                  class="emp-avatar-img" alt="Avatar" @error="avatarError = true" />
                <div v-else class="emp-avatar-initials">{{ initials }}</div>
                <button class="avatar-edit-btn" title="Change photo" @click="openModal('profilePicture')">
                  <font-awesome-icon icon="camera" />
                </button>
              </div>
              <div>
                <h6 class="emp-name">{{ employee.first_name+' '+employee.last_name }}</h6>
                <p class="emp-role">{{ employee.position? employee.position.name : '—' }}</p>
                <span class="status-badge" :class="statusClass(employee?.status || (employee?.is_active ? 'active' : 'inactive'))">
                  {{ employee?.status || (employee?.is_active ? 'Active' : 'Inactive') }}
                </span>
              </div>
            </div>
            <button class="btn-edit-section" @click="openModal('personal')">
              <font-awesome-icon icon="pen" class="me-1" /> Edit
            </button>
          </div>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">First Name</span>
              <span class="detail-value">{{ employee.first_name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Middle Name</span>
              <span class="detail-value">{{ employee.middle_name || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Last Name</span>
              <span class="detail-value">{{ employee.last_name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Official Email</span>
              <span class="detail-value">{{ employee.email }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Personal Email</span>
              <span class="detail-value">{{ employee.personal_email }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Phone</span>
              <span class="detail-value">{{ employee.phone_number || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Date of Birth</span>
              <span class="detail-value">{{ employee.dob || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Gender</span>
              <span class="detail-value">{{ employee.gender || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Highest Qualification</span>
              <span class="detail-value">{{ qualificationLabel(employee.highest_qualification) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Post Code</span>
              <span class="detail-value">{{ employee.post_code || '—' }}</span>
            </div>
            <div class="detail-item detail-item--full">
              <span class="detail-label">Address</span>
              <span class="detail-value">{{ employee.address || '—' }}</span>
            </div>
          </div>
        </div>
  
        <!-- ══════════════════════════════
             NEXT OF KIN
        ══════════════════════════════ -->
        <div class="detail-card mb-5">
          <div class="detail-card-hd">
            <div>
              <h6 class="section-title">Next of Kin</h6>
              <p class="section-sub">Emergency contact information</p>
            </div>
            <button class="btn-edit-section" @click="openModal('nextOfKin')">
              <font-awesome-icon icon="pen" class="me-1" /> Edit
            </button>
          </div>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Full Name</span>
              <span class="detail-value">{{ employee.nextofkin? employee.nextofkin.name : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Relationship</span>
              <span class="detail-value">{{ employee.nextofkin? employee.nextofkin.relationship : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Phone</span>
              <span class="detail-value">{{ employee.nextofkin? employee.nextofkin.phone_number : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Email</span>
              <span class="detail-value">{{ employee.nextofkin? employee.nextofkin.email : '—' }}</span>
            </div>
            <div class="detail-item detail-item--full">
              <span class="detail-label">Address</span>
              <span class="detail-value">{{ employee.nextofkin? employee.nextofkin.address : '—' }}</span>
            </div>
          </div>
        </div>
  
        <!-- ══════════════════════════════
             EMPLOYMENT INFO
        ══════════════════════════════ -->
        <div class="detail-card mb-5">
          <div class="detail-card-hd">
            <div>
              <h6 class="section-title">Employment Information</h6>
              <p class="section-sub">Role, department and work details</p>
            </div>
            <button class="btn-edit-section" @click="openModal('employment')">
              <font-awesome-icon icon="pen" class="me-1" /> Edit
            </button>
          </div>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Role</span>
              <span class="detail-value">{{ employee.role ? employee.role.name : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Department</span>
              <span class="detail-value">{{ employee.department? employee.department.name : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Unit</span>
              <span class="detail-value">{{ employee.unit? employee.unit.name : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Position</span>
              <span class="detail-value">{{ employee.position? employee.position.name : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Pay Grade</span>
              <span class="detail-value">{{ employee.paygrade? employee.paygrade.name : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Location(s)</span>
              <span class="detail-value">
                <template v-if="employee.locations && employee.locations.length">
                  <span v-for="(loc, i) in employee.locations" :key="loc.id">
                    {{ loc.name }}<template v-if="String(loc.id) === String(employee.location_id)"> (Main)</template><template v-if="i < employee.locations.length - 1">, </template>
                  </span>
                </template>
                <template v-else>{{ employee.location ? employee.location.name : '—' }}</template>
              </span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Supervisor</span>
              <span class="detail-value">{{ employee.supervisor? employee.supervisor.first_name+' '+employee.supervisor.last_name : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Employment Date</span>
              <span class="detail-value">{{ employee.hire_date || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Salary Type</span>
              <span class="detail-value">{{ salaryTypeLabel(employee?.salary_type) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Basic Salary</span>
              <span class="detail-value">{{ formatAmount(employee?.basic_salary || employee?.paygrade?.basic_salary) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Weekly Contracted Hours</span>
              <span class="detail-value">{{ employee?.contracted_hours ? employee.contracted_hours + ' hrs' : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Hourly Rate</span>
              <span class="detail-value">{{ employee?.hourly_rate ? '£' + Number(employee.hourly_rate).toFixed(2) : '—' }}</span>
            </div>
          </div>
        </div>

        <!-- ══════════════════════════════
             FINANCIAL INFO
        ══════════════════════════════ -->
        <div class="detail-card mb-5">
          <div class="detail-card-hd">
            <div>
              <h6 class="section-title">Financial Information</h6>
              <p class="section-sub">Bank, pension and tax details</p>
            </div>
            <button class="btn-edit-section" @click="openModal('financial')">
              <font-awesome-icon icon="pen" class="me-1" /> Edit
            </button>
          </div>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Bank</span>
              <span class="detail-value">{{ employee.bank? employee.bank.name : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Account Number</span>
              <span class="detail-value">{{ employee.bank_account_no || '—' }}</span>
            </div>
            <div class="detail-item detail-item--full">
              <span class="detail-label">Account Name</span>
              <span class="detail-value">{{ employee.bank_account_name || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Sort Code</span>
              <span class="detail-value">{{ employee.bank_sortcode || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Pension Provider</span>
              <span class="detail-value">{{ employee.pension_company? employee.pension_company.name : '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Pension Number</span>
              <span class="detail-value">{{ employee.pension_number || '—' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Tax Number</span>
              <span class="detail-value">{{ employee.tax_number || '—' }}</span>
            </div>
          </div>
        </div>
  
        <!-- ══════════════════════════════
             DOCUMENTS
        ══════════════════════════════ -->
        <div class="detail-card mb-5">
          <div class="detail-card-hd">
            <div>
              <h6 class="section-title">Documents</h6>
              <p class="section-sub">Employee uploaded files and records</p>
            </div>
            <button class="btn-primary-action" @click="openModal('uploadDocument')">
              <font-awesome-icon icon="upload" class="me-1" /> Upload Document
            </button>
          </div>
  
          <!-- Empty -->
          <div v-if="!employee.documents || employee.documents.length === 0" class="doc-empty">
            <font-awesome-icon icon="folder-open" class="doc-empty-icon" />
            <p>No documents uploaded yet.</p>
          </div>
  
          <!-- Doc grid -->
          <div v-else class="doc-grid">
            <div v-for="doc in employee.documents" :key="doc.id"
              class="doc-card" @click="openDocumentPreview(doc)">
              <div class="doc-card-icon">
                <font-awesome-icon :icon="docIcon(doc.mime_type || '')" />
              </div>
              <div class="doc-card-body">
                <p class="doc-card-name">{{ doc.description || '—' }}</p>
                <p class="doc-card-type">{{ docTypeLabel(doc.name) }}</p>
              </div>
              <button class="doc-card-delete" title="Delete document"
                @click.stop="promptDeleteDocument(doc)">
                <font-awesome-icon icon="trash" />
              </button>
            </div>
          </div>
        </div>
  
      </template>

      <div v-else class="state-block">
        <p class="text-muted-cell">Employee not found in the current business.</p>
        <button class="btn-outline mt-3" @click="$router.push($url('/payroll/employees'))">Back to Employees</button>
      </div>


      <!-- ══════════════════════════════
           EDIT PERSONAL INFO MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: modals.personal }" @click.self="closeModal('personal')">
        <div class="modal-box modal-box--lg">
          <div class="modal-hd">
            <span class="modal-ttl">Edit Personal Information</span>
            <button class="modal-close-btn" @click="closeModal('personal')"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="savePersonal">
            <div class="modal-bd">
              <div class="fields-grid">
                <div class="field-group">
                  <label class="field-label">First Name <span class="req">*</span></label>
                  <input type="text" class="field-input" v-model="personalForm.first_name" required />
                </div>
                <div class="field-group">
                  <label class="field-label">Middle Name</label>
                  <input type="text" class="field-input" v-model="personalForm.middle_name" />
                </div>
                <div class="field-group">
                  <label class="field-label">Last Name <span class="req">*</span></label>
                  <input type="text" class="field-input" v-model="personalForm.last_name" required />
                </div>
                <div class="field-group">
                  <label class="field-label">Official Email <span class="req">*</span></label>
                  <input type="email" class="field-input" v-model="personalForm.email" required />
                </div>
                <div class="field-group">
                  <label class="field-label">Personal Email </label>
                  <input type="email" class="field-input" v-model="personalForm.personal_email" required />
                </div>
                <div class="field-group">
                  <label class="field-label">Phone</label>
                  <input type="tel" class="field-input" v-model="personalForm.phone_number" />
                </div>
                <div class="field-group">
                  <label class="field-label">Date of Birth</label>
                  <input type="date" class="field-input" v-model="personalForm.dob" />
                </div>
                <div class="field-group">
                  <label class="field-label">Gender</label>
                  <select class="field-input field-select" v-model="personalForm.gender">
                    <option value="">— Select —</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">Highest Qualification</label>
                  <select class="field-input field-select" v-model="personalForm.highest_qualification">
                    <option value="">— Select qualification —</option>
                    <option value="no_formal_education">No Formal Education</option>
                    <option value="primary_school">Primary School</option>
                    <option value="secondary_school">Secondary School / GCSE / O-Level</option>
                    <option value="a_level">A-Levels / WAEC</option>
                    <option value="vocational_cert">Vocational / Technical Certificate</option>
                    <option value="ond">OND / Foundation Degree</option>
                    <option value="hnd">HND / Associate Degree</option>
                    <option value="bachelors">Bachelor's Degree (B.Sc / B.A / B.Ed)</option>
                    <option value="pgd">Postgraduate Diploma (PGD)</option>
                    <option value="masters">Master's Degree (M.Sc / MBA / M.A)</option>
                    <option value="doctorate">Doctorate (PhD / MD)</option>
                    <option value="professional_cert">Professional Certification</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">Post Code</label>
                  <input type="text" class="field-input" v-model="personalForm.post_code" />
                </div>
                <div class="field-group field-group--full">
                  <label class="field-label">Address</label>
                  <textarea class="field-input" rows="3" v-model="personalForm.address"></textarea>
                </div>
              </div>
            </div>
            <div class="modal-ft">
              <button type="button" class="btn-ghost" @click="closeModal('personal')">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="crudLoading">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                {{ crudLoading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
  
      <!-- ══════════════════════════════
           EDIT NEXT OF KIN MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: modals.nextOfKin }" @click.self="closeModal('nextOfKin')">
        <div class="modal-box">
          <div class="modal-hd">
            <span class="modal-ttl">Edit Next of Kin</span>
            <button class="modal-close-btn" @click="closeModal('nextOfKin')"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="saveNextOfKin">
            <div class="modal-bd">
              <div class="fields-grid">
                <div class="field-group">
                  <label class="field-label">Full Name</label>
                  <input type="text" class="field-input" v-model="nextofkin.name" />
                </div>
                <div class="field-group">
                  <label class="field-label">Relationship</label>
                  <select class="field-input field-select" v-model="nextofkin.relationship">
                    <option value="">— Select —</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Parent">Parent</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Child">Child</option>
                    <option value="Friend">Friend</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">Phone</label>
                  <input type="tel" class="field-input" v-model="nextofkin.phone_number" />
                </div>
                <div class="field-group">
                  <label class="field-label">Email</label>
                  <input type="email" class="field-input" v-model="nextofkin.email" />
                </div>
                <div class="field-group field-group--full">
                  <label class="field-label">Address</label>
                  <textarea class="field-input" rows="2" v-model="nextofkin.address"></textarea>
                </div>
              </div>
            </div>
            <div class="modal-ft">
              <button type="button" class="btn-ghost" @click="closeModal('nextOfKin')">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="crudLoading">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                {{ crudLoading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
  
      <!-- ══════════════════════════════
           EDIT EMPLOYMENT INFO MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: modals.employment }" @click.self="closeModal('employment')">
        <div class="modal-box modal-box--lg">
          <div class="modal-hd">
            <span class="modal-ttl">Edit Employment Information</span>
            <button class="modal-close-btn" @click="closeModal('employment')"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="saveEmployment">
            <div class="modal-bd">
              <div class="fields-grid">
                <div class="field-group">
                  <label class="field-label">Role</label>
                  <select class="field-input field-select" v-model="employmentForm.role_id"
                    :disabled="employee && employee.is_super"
                    :title="employee && employee.is_super ? 'Business owner\'s role cannot be changed' : ''">
                    <option value="">— Select role —</option>
                    <option v-for="r in filteredRoles" :key="r.id" :value="r.id">{{ r.name }}</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">Department</label>
                  <select class="field-input field-select" v-model="employmentForm.department_id" @change="onEditDepartmentChange">
                    <option value="">— Select —</option>
                    <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">Unit</label>
                  <select class="field-input field-select" v-model="employmentForm.unit_id"
                    :disabled="!employmentForm.department_id || loadingEditPositions">
                    <option value="">— Select unit —</option>
                    <option v-for="u in editUnits" :key="u.id" :value="u.id">{{ u.name }}</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">Position</label>
                  <select class="field-input field-select" v-model="employmentForm.position_id"
                    :disabled="!employmentForm.department_id || loadingEditPositions">
                    <option value="">— Select position —</option>
                    <option v-for="p in editPositions" :key="p.id" :value="p.id">{{ p.name }}</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">Pay Grade</label>
                  <select class="field-input field-select" v-model="employmentForm.pay_grade_id">
                    <option value="">— Select —</option>
                    <option v-for="pg in paygrades" :key="pg.id" :value="pg.id">{{ pg.name }}</option>
                  </select>
                </div>
                <div class="field-group field-group--full">
                  <label class="field-label">Business Location(s)</label>
                  <div class="loc-checklist">
                    <div v-if="locations.length === 0" class="loc-checklist-empty">No locations available</div>
                    <label v-for="loc in locations" :key="loc.id" class="loc-checklist-item">
                      <input type="checkbox" :value="String(loc.id)" v-model="employmentForm.location_ids" />
                      <span>{{ loc.name }}</span>
                    </label>
                  </div>
                </div>
                <div v-if="employmentForm.location_ids && employmentForm.location_ids.length > 0" class="field-group">
                  <label class="field-label">Main Location <span class="req">*</span></label>
                  <select class="field-input field-select" v-model="employmentForm.location_id">
                    <option value="">— Select main location —</option>
                    <option v-for="id in employmentForm.location_ids" :key="id" :value="id">{{ empLocationName(id) }}</option>
                  </select>
                </div>
                <div class="field-group" v-click-outside="() => supervisorOpen = false">
                  <label class="field-label">Supervisor</label>
                  <div class="ss-wrap">
                    <input
                      class="field-input ss-input"
                      :class="{ 'ss-input--open': supervisorOpen }"
                      :value="supervisorOpen ? supervisorSearch : supervisorLabel"
                      @focus="supervisorOpen = true; supervisorSearch = ''"
                      @input="supervisorSearch = $event.target.value; supervisorOpen = true"
                      placeholder="Search employees..."
                      autocomplete="off"
                    />
                    <span v-if="employmentForm.supervisor_id && !supervisorOpen" class="ss-clear"
                      @mousedown.prevent="employmentForm.supervisor_id = ''; supervisorSearch = ''">×</span>
                    <div v-if="supervisorOpen" class="ss-drop">
                      <div v-if="!filteredSupervisors.length" class="ss-empty">No employees found</div>
                      <div
                        v-for="emp in filteredSupervisors" :key="emp.id"
                        class="ss-option"
                        :class="{ 'ss-option--active': employmentForm.supervisor_id === emp.id }"
                        @mousedown.prevent="employmentForm.supervisor_id = emp.id; supervisorOpen = false; supervisorSearch = ''"
                      >
                        {{ emp.first_name + ' ' + emp.last_name }}
                        <span v-if="emp.employee_no" class="ss-emp-no">{{ emp.employee_no }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="field-group">
                  <label class="field-label">Employment Date</label>
                  <input type="date" class="field-input" v-model="employmentForm.hire_date" />
                </div>
                <div class="field-group">
                  <label class="field-label">Employment Type</label>
                  <select class="field-input field-select" v-model="employmentForm.employment_type">
                    <option value="">— Select —</option>
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="casual">Casual</option>
                    <option value="intern">Intern</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">Salary Type</label>
                  <select class="field-input field-select" v-model="employmentForm.salary_type">
                    <option value="">— Select —</option>
                    <option value="fixed">Fixed Salary</option>
                    <option value="hourly">Hourly</option>
                    <option value="variable">Variable / Commission</option>
                  </select>
                </div>
                <div v-if="employmentForm.salary_type === 'fixed'" class="field-group">
                  <label class="field-label">Basic Salary <span class="req">*</span></label>
                  <input type="text" oninput="this.value=this.value.replace(/[^0-9]/g,'')" class="field-input" v-model="employmentForm.basic_salary"
                    :class="{ 'field-input--error': employmentErrors.basic_salary }" />
                  <span v-if="employmentErrors.basic_salary" class="field-error">{{ employmentErrors.basic_salary }}</span>
                </div>
                <div class="field-group">
                  <label class="field-label">Weekly Contracted Hours</label>
                  <input type="number" min="0" max="168" step="0.5" class="field-input" v-model="employmentForm.contracted_hours" placeholder="e.g. 37.5" />
                </div>
                <div v-if="['hourly', 'variable', 'calculated'].includes(employmentForm.salary_type)" class="field-group">
                  <label class="field-label">Hourly Rate <span class="req">*</span></label>
                  <input type="number" min="0" step="0.01" class="field-input" v-model="employmentForm.hourly_rate" placeholder="e.g. 12.50"
                    :class="{ 'field-input--error': employmentErrors.hourly_rate }" />
                  <span v-if="employmentErrors.hourly_rate" class="field-error">{{ employmentErrors.hourly_rate }}</span>
                </div>
              </div>
            </div>
            <div class="modal-ft">
              <button type="button" class="btn-ghost" @click="closeModal('employment')">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="crudLoading">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                {{ crudLoading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
  
      <!-- ══════════════════════════════
           EDIT FINANCIAL INFO MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: modals.financial }" @click.self="closeModal('financial')">
        <div class="modal-box modal-box--lg">
          <div class="modal-hd">
            <span class="modal-ttl">Edit Financial Information</span>
            <button class="modal-close-btn" @click="closeModal('financial')"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="saveFinancial">
            <div class="modal-bd">
              <div class="fields-grid">
                <div class="field-group">
                  <label class="field-label">Bank</label>
                  <select class="field-input field-select" v-model="financialForm.all_bank_id">
                    <option value="">— Select bank —</option>
                    <option v-for="b in banks" :key="b.id" :value="b.id">{{ b.name }}</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">Account Number</label>
                  <input type="text" class="field-input"
                    :class="{ 'field-input--error': financialErrors.bank_account_no }"
                    :value="financialForm.bank_account_no"
                    inputmode="numeric"
                    :placeholder="businessCountryCode === 'GB' ? '8-digit account number' : ''"
                    @input="onAccountNoInput($event)"
                  />
                  <span v-if="financialErrors.bank_account_no" class="field-error">{{ financialErrors.bank_account_no }}</span>
                </div>
                <div class="field-group field-group--full">
                  <label class="field-label">Account Name</label>
                  <input type="text" class="field-input" v-model="financialForm.bank_account_name" />
                </div>
                <div class="field-group">
                  <label class="field-label">Sort Code</label>
                  <input type="text" class="field-input"
                    :class="{ 'field-input--error': financialErrors.bank_sortcode }"
                    :value="financialForm.bank_sortcode"
                    :placeholder="businessCountryCode === 'GB' ? 'e.g. 12-34-56' : ''"
                    @input="onSortCodeInput($event)"
                  />
                  <span v-if="financialErrors.bank_sortcode" class="field-error">{{ financialErrors.bank_sortcode }}</span>
                </div>
                <div class="field-group">
                  <label class="field-label">Pension Provider</label>
                  <select class="field-input field-select" v-model="financialForm.pension_company_id">
                    <option value="">— Select —</option>
                    <option v-for="p in pensionProviders" :key="p.id" :value="p.id">{{ p.name }}</option>
                  </select>
                </div>
                <div class="field-group">
                  <label class="field-label">Pension Number</label>
                  <input type="text" class="field-input" v-model="financialForm.pension_number" />
                </div>
                <div class="field-group">
                  <label class="field-label">Tax Number</label>
                  <input type="text" class="field-input" v-model="financialForm.tax_number" />
                </div>
              </div>
            </div>
            <div class="modal-ft">
              <button type="button" class="btn-ghost" @click="closeModal('financial')">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="crudLoading">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                {{ crudLoading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
  
      <!-- ══════════════════════════════
           CHANGE PROFILE PICTURE MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: modals.profilePicture }" @click.self="closeModal('profilePicture')">
        <div class="modal-box">
          <div class="modal-hd">
            <span class="modal-ttl">Change Profile Photo</span>
            <button class="modal-close-btn" @click="closeModal('profilePicture')"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="modal-bd">
            <div class="avatar-upload-zone" @click="$refs.avatarInput.click()">
              <img v-if="newAvatarPreview" :src="newAvatarPreview" class="avatar-preview-img" alt="Preview" />
              <div v-else class="avatar-upload-placeholder">
                <font-awesome-icon icon="camera" class="avatar-upload-icon" />
                <p>Click to choose a photo</p>
                <p class="upload-hint">JPG, PNG or GIF. Max 2 MB.</p>
              </div>
            </div>
            <input ref="avatarInput" type="file" accept="image/*" style="display:none" @change="onNewAvatarChange" />
          </div>
          <div class="modal-ft">
            <button type="button" class="btn-ghost" @click="closeModal('profilePicture')">Cancel</button>
            <button type="button" class="btn-primary-action" :disabled="!newAvatarFile || crudLoading" @click="saveProfilePicture">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              {{ crudLoading ? 'Uploading...' : 'Save Photo' }}
            </button>
          </div>
        </div>
      </div>
  
  
      <!-- ══════════════════════════════
           UPLOAD DOCUMENT MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: modals.uploadDocument }" @click.self="closeModal('uploadDocument')">
        <div class="modal-box">
          <div class="modal-hd">
            <span class="modal-ttl">Upload Document</span>
            <button class="modal-close-btn" @click="closeModal('uploadDocument')"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="modal-bd">
            <div class="field-group">
              <label class="field-label">Document Type <span class="req">*</span></label>
              <select class="field-input field-select" v-model="docUploadForm.type">
                <option value="">— Select type —</option>
                <option value="national_id">National ID</option>
                <option value="passport">Passport</option>
                <option value="drivers_license">Driver's Licence</option>
                <option value="birth_certificate">Birth Certificate</option>
                <option value="degree_certificate">Degree Certificate</option>
                <option value="professional_cert">Professional Certificate</option>
                <option value="resume">CV / Resume</option>
                <option value="employment_contract">Employment Contract</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Label</label>
              <input type="text" class="field-input" v-model="docUploadForm.label" placeholder="e.g. NIN Card" />
            </div>
            <div class="field-group">
              <label class="field-label">File <span class="req">*</span></label>
              <label class="doc-file-btn" :class="{ 'doc-file-btn--chosen': docUploadForm.file }">
                <font-awesome-icon :icon="docUploadForm.file ? 'file-alt' : 'paperclip'" class="me-1" />
                {{ docUploadForm.file ? docUploadForm.file.name : 'Choose file...' }}
                <input type="file" style="display:none"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.csv"
                  @change="docUploadForm.file = $event.target.files[0]" />
              </label>
            </div>
          </div>
          <div class="modal-ft">
            <button type="button" class="btn-ghost" @click="closeModal('uploadDocument')">Cancel</button>
            <button type="button" class="btn-primary-action"
              :disabled="!docUploadForm.type || !docUploadForm.file || crudLoading"
              @click="uploadDocument">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              {{ crudLoading ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </div>
      </div>
  
  
      <!-- ══════════════════════════════
           DOCUMENT PREVIEW MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: modals.documentPreview }" @click.self="closeModal('documentPreview')">
        <div class="modal-box modal-box--lg">
          <div class="modal-hd">
            <span class="modal-ttl">{{ selectedDoc?.file_name || selectedDoc?.description || 'Document' }}</span>
            <button class="modal-close-btn" @click="closeModal('documentPreview')"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="modal-bd" style="min-height:300px; display:flex; align-items:center; justify-content:center;">
            <template v-if="selectedDoc">
              <img v-if="isImageDoc(selectedDoc.mime_type)" :src="selectedDoc.file_url"
                style="max-width:100%;max-height:70vh;border-radius:8px;" alt="Document" />
              <iframe v-else-if="selectedDoc.mime_type === 'application/pdf'" :src="selectedDoc.file_url"
                style="width:100%;height:65vh;border:none;border-radius:8px;" />
              <div v-else class="doc-no-preview">
                <font-awesome-icon icon="file-alt" style="font-size:48px;color:#94a3b8;" />
                <p style="margin:12px 0 4px;font-weight:600;color:#1a2b45;">No preview available</p>
                <a :href="selectedDoc.file_url" target="_blank" class="btn-primary-action" style="margin-top:8px;">
                  <font-awesome-icon icon="download" class="me-1" /> Download File
                </a>
              </div>
            </template>
          </div>
        </div>
      </div>
  
  
      <!-- ══════════════════════════════
           DELETE DOCUMENT CONFIRM MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: modals.deleteDocument }" @click.self="closeModal('deleteDocument')">
        <div class="modal-box modal-box--sm">
          <div class="modal-icon-wrap">
            <div class="modal-icon modal-icon--red"><font-awesome-icon icon="trash" /></div>
          </div>
          <div class="modal-body-clean">
            <p class="modal-heading">Delete Document?</p>
            <p class="modal-subtext">
              Are you sure you want to delete
              <strong>{{ documentToDelete?.fileName || documentToDelete?.label }}</strong>?
              This action cannot be undone.
            </p>
          </div>
          <div class="modal-actions">
            <button class="modal-btn modal-btn--cancel" @click="closeModal('deleteDocument')">Cancel</button>
            <button class="modal-btn modal-btn--danger" :disabled="crudLoading" @click="confirmDeleteDocument">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
              {{ crudLoading ? 'Deleting...' : 'Yes, Delete' }}
            </button>
          </div>
        </div>
      </div>
  
  
       <!-- ══════════════════════════════
           UPDATE STATUS MODAL
      ══════════════════════════════ -->
      <div class="modal-overlay" :class="{ open: statusOpen }" @click.self="statusOpen = false">
        <div class="modal-box">
          <div class="modal-hd">
            <span class="modal-ttl">Update Employee Status</span>
            <button class="modal-close-btn" @click="statusOpen = false"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="submitStatusUpdate">
            <div class="modal-bd">
              <div class="field-group">
                <label class="field-label">Current Status</label>
                <div style="padding:8px 0;">
                    <span class="status-badge status-badge-lg"
                    :class="statusClass(employee?.status || (employee?.is_active ? 'active' : 'inactive'))">
                    {{ employee?.status || (employee?.is_active ? 'Active' : 'Inactive') }}
                  </span>
                </div>
              </div>
              <div class="field-group">
                <label class="field-label">New Status <span class="req">*</span></label>
                <select class="field-input field-select" v-model="statusForm.status" required>
                  <option value="">— Select status —</option>
                  <!-- <option value="pending">Pending</option>
                  <option value="invited">Invited</option> -->
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="on-leave">On Leave</option>
                  <option value="terminated">Terminated</option>
                  <option value="resigned">Resigned</option>
                </select>
              </div>

              <div v-if="statusForm.status == 'terminated' || statusForm.status == 'resigned' || statusForm.status == 'suspended'" class="field-group">
                <label class="field-label">
                    {{ statusForm.status == 'terminated' ? 'Termination' : statusForm.status == 'suspended' ? 'Suspension' : statusForm.status == 'resigned' ? 'Resignation' : '' }} Date
                </label>
                <input type="date" class="field-input" v-model="statusForm.event_date" />
              </div>

              <div class="field-group">
                <label class="field-label">Reason / Note</label>
                <textarea class="field-input" rows="3" v-model="statusForm.reason"
                  placeholder="Optional note for this status change"></textarea>
              </div>
            </div>
            <div class="modal-ft">
              <button type="button" class="btn-ghost" @click="statusOpen = false">Cancel</button>
              <button type="submit" class="btn-primary-action"
                :disabled="!statusForm.status || crudLoading">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                {{ crudLoading ? 'Updating...' : 'Update Status' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
      <!-- Backdrop -->
      <div v-if="anyModalOpen" class="modal-backdrop-custom" />
  
    </div>
  </template>
  
  
  <script>
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import hostName from '../../../../main';
  
  export default {
    name: 'EmployeeDetailsPage',
  
    components: { Header },
  
    data() {
      return {
        store: useStore(),
        employee:  null,
        loading:   false,
        crudLoading: false,
        avatarError: false,
        url: hostName,
  
        // Lookup lists for edit modals
        departments:    [],
        paygrades:      [],
        banks:          [],
        pensionProviders: [],
        locations:      [],
        allEmployees:   [],
        supervisorSearch: '',
        supervisorOpen:   false,
        roles:          [],
        editPositions:  [],
        editUnits:      [],
        loadingEditPositions: false,
  
        // Modal visibility flags
        modals: {
          personal:        false,
          nextOfKin:       false,
          employment:      false,
          financial:       false,
          profilePicture:  false,
          uploadDocument:  false,
          documentPreview: false,
          deleteDocument:  false,
        },
        statusOpen: false,
  
        // Edit forms — pre-filled when modal opens
        personalForm:   {},
        nextofkin:        {},
        employmentForm: { location_ids: [], location_id: '' },
        employmentErrors: {},
        financialForm:  {},
        financialErrors: {},
        businessCountryCode: '',

  
        // Profile picture
        newAvatarFile:    null,
        newAvatarPreview: null,
  
        // Document upload
        docUploadForm: { type: '', label: '', file: null },
  
        // Document actions
        selectedDoc:      null,
        documentToDelete: null,
  
        // Status update
        statusForm: { status: '', event_date: '', reason: '' },
  
        toast: useToast(),
      };
    },
  
    directives: {
      clickOutside: {
        mounted(el, binding) {
          el._coHandler = e => { if (!el.contains(e.target)) binding.value(e); };
          document.addEventListener('mousedown', el._coHandler);
        },
        unmounted(el) { document.removeEventListener('mousedown', el._coHandler); },
      },
    },

    computed: {
      isSuperAdmin() { return this.store.state.getLoggedInUser?.is_super === 1; },

      filteredSupervisors() {
        const q = this.supervisorSearch.trim().toLowerCase();
        if (!q) return this.allEmployees;
        return this.allEmployees.filter(e =>
          `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
          (e.employee_no || '').toLowerCase().includes(q)
        );
      },

      supervisorLabel() {
        if (!this.employmentForm.supervisor_id) return '';
        const emp = this.allEmployees.find(e => e.id === this.employmentForm.supervisor_id);
        return emp ? `${emp.first_name} ${emp.last_name}` : '';
      },
      filteredRoles() {
        if (this.isSuperAdmin) return this.roles;
        return this.roles.filter(r => r.slug !== 'business_owner');
      },

      initials() {
        if (!this.employee) return '?';
        const f = this.employee.first_name?.charAt(0) || '';
        const l = this.employee.last_name?.charAt(0)  || '';
        return (f + l).toUpperCase() || '?';
      },
  
      anyModalOpen() {
        return Object.values(this.modals).some(Boolean) || this.statusOpen;
      },
    },
  
    created() {
      this.fetchEmployee();
      this.fetchLookups();
    },

    beforeUnmount() {
      document.body.style.overflow = '';
    },
  
    methods: {
      empLocationName(id) {
        const loc = this.locations.find(l => String(l.id) === String(id));
        return loc ? loc.name : id;
      },

      removeEmpLocation(id) {
        this.employmentForm.location_ids = this.employmentForm.location_ids.filter(l => String(l) !== String(id));
        if (String(this.employmentForm.location_id) === String(id)) this.employmentForm.location_id = '';
      },


      // ── Data fetching ──────────────────────────────────────────────────
      fetchEmployee() {
        this.loading = true;
        const id = this.$route.params.id;
        axios.get(`/payroll/employees/details/${id}`)
          .then(({ data }) => {
            this.employee = data?.data?.employee || data?.data || null;
            this.avatarError = false;
          })
          .catch(err => {
            this.employee = null;
            this.toast.error(err.response?.data?.message ?? 'Could not load employee.');
          })
          .finally(() => { this.loading = false; });
      },
  
      fetchLookups() {
        axios.get('/admin/business/user/active-business')
          .then(res => { this.businessCountryCode = res.data?.data?.country_code ?? ''; })
          .catch(() => {});

        Promise.allSettled([
          axios.get('/payroll/department/active',         { params: { status: [1], length: 200 } }),
          axios.get('/payroll/paygrade/active'),
          axios.get('/settings/allbank/active'),
          axios.get('/settings/pension-provider/active'),
          axios.get('/admin/location/active'),
          axios.get('/payroll/employees/active'),
          axios.get('/admin/role'),
        ]).then(([depts, grades, banks, pensions, locs, emps, roles]) => {
          if (depts.status    === 'fulfilled') this.departments     = this.normalise(depts.value.data);
          if (grades.status   === 'fulfilled') this.paygrades       = this.normalise(grades.value.data);
          if (banks.status    === 'fulfilled') this.banks           = this.normalise(banks.value.data);
          if (pensions.status === 'fulfilled') this.pensionProviders = this.normalise(pensions.value.data);
          if (locs.status     === 'fulfilled') this.locations = this.normalise(locs.value.data);
          else console.error('Locations fetch failed:', locs.reason);
          if (emps.status     === 'fulfilled') this.allEmployees    = this.normalise(emps.value.data);
          if (roles.status    === 'fulfilled') this.roles           = this.normalise(roles.value.data);
        });
      },
  
      normalise(data) {
        return Array.isArray(data?.data?.data) ? data.data.data
          : Array.isArray(data?.data) ? data.data : [];
      },
  
      onEditDepartmentChange() {
        this.employmentForm.position_id = '';
        this.employmentForm.unit_id     = '';
        this.editPositions = [];
        this.editUnits     = [];
        if (!this.employmentForm.department_id) return;
        this.loadingEditPositions = true;
        Promise.allSettled([
          axios.get('/payroll/position/active', { params: { department_id: this.employmentForm.department_id } }),
          axios.get('/payroll/unit/active',     { params: { department_id: this.employmentForm.department_id } }),
        ]).then(([pos, units]) => {
          if (pos.status   === 'fulfilled') this.editPositions = this.normalise(pos.value.data);
          if (units.status === 'fulfilled') this.editUnits     = this.normalise(units.value.data);
        }).finally(() => { this.loadingEditPositions = false; });
      },

      onCurrentDepartmentChange() {
        this.editPositions = [];
        this.editUnits     = [];
        if (!this.employee.department_id) return;
        this.loadingEditPositions = true;
        Promise.allSettled([
          axios.get('/payroll/position/active', { params: { department_id: this.employee.department_id } }),
          axios.get('/payroll/unit/active',     { params: { department_id: this.employee.department_id } }),
        ]).then(([pos, units]) => {
          if (pos.status   === 'fulfilled') this.editPositions = this.normalise(pos.value.data);
          if (units.status === 'fulfilled') this.editUnits     = this.normalise(units.value.data);
        }).finally(() => { this.loadingEditPositions = false; });
      },
  
     
      // ── Modal helpers ──────────────────────────────────────────────────
      openModal(name) {
        document.body.style.overflow = 'hidden';
        if (name === 'personal') {
          this.personalForm = {
            first_name:             this.employee.first_name,
            middle_name:            this.employee.middle_name,
            last_name:              this.employee.last_name,
            email:                  this.employee.email,
            personal_email:         this.employee.personal_email,
            phone_number:           this.employee.phone_number,
            dob:                    this.employee.dob,
            gender:                 this.employee.gender,
            highest_qualification:  this.employee.highest_qualification,
            address:                this.employee.address,
            post_code:              this.employee.post_code,
          };
        }
        if (name === 'nextOfKin') {
          this.nextofkin = {
            name:         this.employee?.nextofkin?.name,
            relationship: this.employee?.nextofkin?.relationship,
            phone_number:        this.employee?.nextofkin?.phone_number,
            email:        this.employee?.nextofkin?.email,
            address:      this.employee?.nextofkin?.address,
          };
        }
        if (name === 'employment') {
            if (this.employee.department_id) this.onCurrentDepartmentChange();
          this.employmentErrors = {};
          this.employmentForm = {
            role_id:          this.employee.role_id,
            department_id:   this.employee.department_id,
            unit_id:         this.employee.unit_id,
            position_id:     this.employee.position_id,
            pay_grade_id:    this.employee.pay_grade_id,
            location_id:     this.employee.location_id ? String(this.employee.location_id) : '',
            location_ids:    (this.employee.locations && this.employee.locations.length)
              ? this.employee.locations.map(l => String(l.id))
              : (this.employee.location_id ? [String(this.employee.location_id)] : []),
            supervisor_id:   this.employee.supervisor_id,
            hire_date:       this.employee.hire_date,
            employment_type:  this.employee.employment_type,
            salary_type:      this.employee.salary_type === 'calculated' ? 'hourly' : (this.employee.salary_type || 'fixed'),
            basic_salary:     this.employee.basic_salary,
            contracted_hours: this.employee.contracted_hours,
            hourly_rate:      this.employee.hourly_rate,
          };
          // Pre-load positions/units for the current department
        //   if (this.employee.department_id) this.onEditDepartmentChange();
        }
        if (name === 'financial') {
          this.financialErrors = {};
          const fullName = [this.employee.first_name, this.employee.middle_name, this.employee.last_name]
            .filter(Boolean).join(' ');
          this.financialForm = {
            all_bank_id:        this.employee.all_bank_id,
            bank_account_no:    this.employee.bank_account_no,
            bank_account_name:  this.employee.bank_account_name || fullName,
            bank_sortcode:      this.employee.bank_sortcode,
            pension_company_id: this.employee.pension_company_id,
            pension_number:     this.employee.pension_number,
            tax_number:         this.employee.tax_number,
          };
        }

        if (name === 'profilePicture') {
            this.newAvatarFile    = null;
            this.newAvatarPreview = null;
            // Reset file input so onChange fires even if the same file is re-selected
            this.$nextTick(() => {
            const input = this.$refs.avatarInput;
            if (input) input.value = '';
            });
        }

        if (name === 'uploadDocument') {
          this.docUploadForm = { type: '', label: '', file: null };
        }
        this.modals[name] = true;
      },
  
      closeModal(name) {
        this.modals[name] = false;
        if (!Object.values(this.modals).some(Boolean)) {
          document.body.style.overflow = '';
        }
      },
  
      // ── CRUD: Personal ─────────────────────────────────────────────────
      savePersonal() {
        this.crudLoading = true;
        axios.put(`/payroll/employees/${this.employee.id}`, this.personalForm)
          .then(() => {
            this.toast.success('Personal information updated.');
            this.closeModal('personal');
            this.fetchEmployee();
          })
          .catch(err => this.toast.error(this.flatError(err.response?.data) ?? 'Could not update.'))
          .finally(() => { this.crudLoading = false; });
      },
  
      // ── CRUD: Next of Kin ──────────────────────────────────────────────
      saveNextOfKin() {
        this.crudLoading = true;
        axios.put(`/payroll/employees/${this.employee.id}/nextofkin`, this.nextofkin)
          .then(() => {
            this.toast.success('Next of kin updated.');
            this.closeModal('nextOfKin');
            this.fetchEmployee();
          })
          .catch(err => this.toast.error(this.flatError(err.response?.data) ?? 'Could not update.'))
          .finally(() => { this.crudLoading = false; });
      },
  
      // ── CRUD: Employment ───────────────────────────────────────────────
      saveEmployment() {
        const errs = {};
        if (this.employmentForm.salary_type === 'fixed' && !this.employmentForm.basic_salary)
          errs.basic_salary = 'Basic salary is required for fixed salary type.';
        if (['hourly', 'variable', 'calculated'].includes(this.employmentForm.salary_type) && !this.employmentForm.hourly_rate)
          errs.hourly_rate = 'Hourly rate is required for this salary type.';
        this.employmentErrors = errs;
        if (Object.keys(errs).length) return;

        const payload = { ...this.employmentForm };
        if (['hourly', 'variable', 'calculated'].includes(payload.salary_type)) payload.basic_salary = null;
        if (payload.salary_type === 'fixed') payload.hourly_rate = null;

        this.crudLoading = true;
        axios.put(`/payroll/employees/${this.employee.id}/employment`, payload)
          .then(() => {
            this.toast.success('Employment information updated.');
            this.closeModal('employment');
            this.fetchEmployee();
          })
          .catch(err => this.toast.error(this.flatError(err.response?.data) ?? 'Could not update.'))
          .finally(() => { this.crudLoading = false; });
      },
  
      // ── CRUD: Financial ────────────────────────────────────────────────
      formatSortCode(raw) {
        const digits = (raw || '').replace(/\D/g, '').slice(0, 6);
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0,2)}-${digits.slice(2)}`;
        return `${digits.slice(0,2)}-${digits.slice(2,4)}-${digits.slice(4)}`;
      },

      onAccountNoInput(e) {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
        this.financialForm.bank_account_no = digits;
        e.target.value = digits;
        this.financialErrors.bank_account_no = '';
      },

      onSortCodeInput(e) {
        const formatted = this.formatSortCode(e.target.value);
        this.financialForm.bank_sortcode = formatted;
        e.target.value = formatted;
        this.financialErrors.bank_sortcode = '';
      },

      saveFinancial() {
        this.financialErrors = {};

        if (this.businessCountryCode === 'GB') {
          const accountNo = (this.financialForm.bank_account_no || '').trim();
          const sortCode  = (this.financialForm.bank_sortcode   || '').trim();

          if (accountNo && !/^\d{8}$/.test(accountNo)) {
            this.financialErrors.bank_account_no = 'UK account number must be exactly 8 digits.';
          }

          const scDigits = sortCode.replace(/\D/g, '');
          if (sortCode && scDigits.length !== 6) {
            this.financialErrors.bank_sortcode = 'UK sort code must be 6 digits (e.g. 12-34-56).';
          } else if (sortCode && scDigits.length === 6) {
            this.financialForm.bank_sortcode = this.formatSortCode(scDigits);
          }

          if (Object.values(this.financialErrors).some(Boolean)) return;
        }

        this.crudLoading = true;
        axios.put(`/payroll/employees/${this.employee.id}/financial`, this.financialForm)
          .then(() => {
            this.toast.success('Financial information updated.');
            this.closeModal('financial');
            this.fetchEmployee();
          })
          .catch(err => this.toast.error(this.flatError(err.response?.data) ?? 'Could not update.'))
          .finally(() => { this.crudLoading = false; });
      },
  
      // ── CRUD: Profile picture ──────────────────────────────────────────
      onNewAvatarChange(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        this.newAvatarFile = file;
        const reader = new FileReader();
        reader.onloadend = () => { this.newAvatarPreview = reader.result; };
        reader.readAsDataURL(file);
      },
  
      saveProfilePicture() {
        if (!this.newAvatarFile) {
            this.toast.error('Please select a photo before saving.');
            return;
        }
        // if (!this.newAvatarFile) return;
        this.crudLoading = true;
        const fd = new FormData();
        fd.append('avatar', this.newAvatarFile);
        axios.post(`/payroll/employees/${this.employee.id}/avatar`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
          .then(() => {
            this.toast.success('Profile photo updated.');
            this.closeModal('profilePicture');
            this.fetchEmployee();
          })
          .catch(err => this.toast.error(this.flatError(err.response?.data) ?? 'Could not update photo.'))
          .finally(() => { this.crudLoading = false; });
      },
  
      // ── CRUD: Upload document ──────────────────────────────────────────
      uploadDocument() {
        if (!this.docUploadForm.type || !this.docUploadForm.file) return;
        this.crudLoading = true;
        const fd = new FormData();
        fd.append('name',  this.docUploadForm.type);
        fd.append('description', this.docUploadForm.label || '');
        fd.append('file',  this.docUploadForm.file, this.docUploadForm.file.name);
        axios.post(`/payroll/employees/${this.employee.id}/documents`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
          .then(() => {
            this.toast.success('Document uploaded.');
            this.closeModal('uploadDocument');
            this.fetchEmployee();
          })
          .catch(err => this.toast.error(this.flatError(err.response?.data) ?? 'Could not upload document.'))
          .finally(() => { this.crudLoading = false; });
      },
  
      // ── Document preview & delete ──────────────────────────────────────
      openDocumentPreview(doc) {
        this.selectedDoc = doc;
        this.modals.documentPreview = true;
      },
  
      promptDeleteDocument(doc) {
        this.documentToDelete = doc;
        this.modals.deleteDocument = true;
      },
  
      confirmDeleteDocument() {
        if (!this.documentToDelete) return;
        this.crudLoading = true;
        axios.delete(`/payroll/employees/documents/${this.documentToDelete.id}`)
          .then(() => {
            this.toast.success('Document deleted.');
            this.closeModal('deleteDocument');
            this.documentToDelete = null;
            this.fetchEmployee();
          })
          .catch(err => this.toast.error(this.flatError(err.response?.data) ?? 'Could not delete document.'))
          .finally(() => { this.crudLoading = false; });
      },
  
      // ── Status update ──────────────────────────────────────────────────
      submitStatusUpdate() {
        if (!this.statusForm.status) return;
        this.crudLoading = true;
        axios.put(`/payroll/employees/${this.employee.id}/status`, {
          status: this.statusForm.status,
          event_date: this.statusForm.event_date || '',
          reason: this.statusForm.reason || '',
        })
          .then(() => {
            this.toast.success(`Employee status updated to "${this.statusForm.status}".`);
            this.statusOpen = false;
            this.statusForm = { status: '', reason: '' };
            this.fetchEmployee();
          })
          .catch(err => this.toast.error(this.flatError(err.response?.data) ?? 'Could not update status.'))
          .finally(() => { this.crudLoading = false; });
      },
  
      // ── Helpers ────────────────────────────────────────────────────────
      statusClass(status) {
        const map = {
          active:      'status-badge--active',
          invited:     'status-badge--invited',
          pending:     'status-badge--pending',
          suspended:   'status-badge--suspended',
          'on-leave':  'status-badge--leave',
          terminated:  'status-badge--terminated',
          resigned:    'status-badge--resigned',
        };
        return map[status] ?? 'status-badge--pending';
      },

      flatError(data) {
        if (!data || typeof data !== 'object') return null;
        const msg = data.message;
        if (typeof msg === 'string') return msg;
        if (Array.isArray(msg)) return msg[0];
        if (typeof msg === 'object') return Object.values(msg).flat()[0] ?? null;
        const errs = data.data;
        if (errs && typeof errs === 'object') return Object.values(errs).flat()[0] ?? null;
        return null;
      },
  
      docTypeLabel(type) {
        const map = {
          national_id: 'National ID', passport: 'Passport',
          drivers_license: "Driver's Licence", birth_certificate: 'Birth Certificate',
          degree_certificate: 'Degree Certificate', professional_cert: 'Professional Cert',
          resume: 'CV / Resume', employment_contract: 'Employment Contract', other: 'Other',
        };
        return map[type] ?? type;
      },

      qualificationLabel(value) {
        const map = {
          no_formal_education: 'No Formal Education',
          primary_school:      'Primary School',
          secondary_school:    'Secondary School / GCSE / O-Level',
          a_level:             'A-Levels / WAEC',
          vocational_cert:     'Vocational / Technical Certificate',
          ond:                 'OND / Foundation Degree',
          hnd:                 'HND / Associate Degree',
          bachelors:           "Bachelor's Degree",
          pgd:                 'Postgraduate Diploma (PGD)',
          masters:             "Master's Degree",
          doctorate:           'Doctorate (PhD / MD)',
          professional_cert:   'Professional Certification',
          other:               'Other',
        };
        return value ? (map[value] ?? value) : '—';
      },
  
      docIcon(mime) {
        if (!mime) return 'file-alt';
        if (mime.startsWith('image/'))    return 'file-image';
        if (mime === 'application/pdf')   return 'file-pdf';
        if (mime.includes('word'))        return 'file-word';
        if (mime.includes('sheet') || mime.includes('excel')) return 'file-excel';
        return 'file-alt';
      },
  
      isImageDoc(mime) {
        return mime?.startsWith('image/');
      },

      salaryTypeLabel(value) {
        const map = { fixed: 'Fixed Salary', hourly: 'Hourly', variable: 'Variable / Commission', calculated: 'Hourly' };
        return value ? (map[value] ?? value) : '—';
      },

      formatAmount(value) {
        return Number(value).toLocaleString('en-NG', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
      },
    },
  };
  </script>
  
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .back-row    { margin-bottom:12px; }
  .btn-back    { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; font-size:13px; font-weight:600; color:#7a90b0; cursor:pointer; padding:0; transition:color .15s; }
  .btn-back:hover { color:#004AAD; }
  .page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
  .breadcrumb-link { color:#7a90b0; text-decoration:none; }
  .breadcrumb-link:hover { color:#004AAD; }
  .breadcrumb-sep { color:#c5d0e0; }
  .page-actions { display:flex; gap:8px; }
  .mb-5 { margin-bottom:20px; }
  .me-1 { margin-right:4px; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; font-family:inherit; text-decoration:none; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
  .btn-ghost:hover { background:#e4eaf2; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; font-family:inherit; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-danger-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#e03535; border:1px solid #fecaca; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; font-family:inherit; }
  .btn-danger-outline:hover:not(:disabled) { background:#fff5f5; }
  .btn-danger-outline:disabled { opacity:.45; cursor:not-allowed; }
  .btn-edit-section { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; font-size:12.5px; font-weight:600; padding:7px 14px; border-radius:8px; cursor:pointer; transition:background .15s; font-family:inherit; white-space:nowrap; }
  .btn-edit-section:hover { background:#e4eaf2; }
  
  /* ── State ───────────────────────────────────────────────────────────── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 24px; text-align:center; }
  .text-muted-cell { color:#4a5e78; }
  .mt-3 { margin-top:12px; }
  
  /* ── Detail card ─────────────────────────────────────────────────────── */
  .detail-card    { background:#fff; border:1px solid #e4eaf2; border-radius:14px; overflow:hidden; }
  .detail-card-hd { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; padding:20px 24px; border-bottom:1px solid #e4eaf2; }
  .detail-card-hd-left { display:flex; align-items:center; gap:16px; }
  .section-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub    { font-size:12.5px; color:#7a90b0; margin:0; }
  
  /* ── Avatar ──────────────────────────────────────────────────────────── */
  .emp-avatar-wrap    { position:relative; flex-shrink:0; }
  .emp-avatar-img     { width:64px; height:64px; border-radius:50%; object-fit:cover; border:3px solid #e4eaf2; }
  .emp-avatar-initials { width:64px; height:64px; border-radius:50%; background:#004AAD; color:#fff; font-size:20px; font-weight:800; display:flex; align-items:center; justify-content:center; }
  .avatar-edit-btn    { position:absolute; bottom:-2px; right:-2px; width:22px; height:22px; border-radius:50%; background:#004AAD; color:#fff; border:2px solid #fff; font-size:9px; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background .15s; }
  .avatar-edit-btn:hover { background:#003b8e; }
  .emp-name  { font-size:17px; font-weight:800; color:#1a2b45; margin:0 0 3px; }
  .emp-role  { font-size:13px; color:#7a90b0; margin:0 0 6px; }
  .status-badge         { display:inline-block; font-size:11.5px; font-weight:700; border-radius:20px; padding:2px 10px; text-transform:capitalize; }
  .status-badge--active     { background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; }
  .status-badge--inactive   { background:#fff0f0; color:#e03535; border:1px solid #fecaca; }
  .status-badge--invited    { background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; }
  .status-badge--pending    { background:#fefce8; color:#ca8a04; border:1px solid #fde68a; }
  .status-badge--suspended  { background:#fff7ed; color:#ea580c; border:1px solid #fed7aa; }
  .status-badge--leave      { background:#f5f3ff; color:#7c3aed; border:1px solid #ddd6fe; }
  .status-badge--terminated { background:#fff0f0; color:#e03535; border:1px solid #fecaca; }
  .status-badge--resigned   { background:#f4f7fb; color:#64748b; border:1px solid #e4eaf2; }
  
  /* ── Status modal ────────────────────────────────────────────────────── */
  .current-status-display { padding:10px 0 4px; }
  .status-badge-lg { font-size:13px; font-weight:700; border-radius:20px; padding:5px 16px; text-transform:capitalize; }
  
  /* ── Detail grid ─────────────────────────────────────────────────────── */
  .detail-grid       { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; padding:24px; }
  .detail-item       { display:flex; flex-direction:column; gap:4px; }
  .detail-item--full { grid-column:1/-1; }
  .detail-label      { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#7a90b0; }
  .detail-value      { font-size:14px; color:#1a2b45; font-weight:500; }
  
  /* ── Documents ───────────────────────────────────────────────────────── */
  .doc-empty      { display:flex; flex-direction:column; align-items:center; padding:48px; text-align:center; }
  .doc-empty-icon { font-size:36px; color:#c0c9d8; margin-bottom:12px; }
  .doc-empty p    { font-size:14px; font-weight:600; color:#7a90b0; margin:0; }
  .doc-grid       { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:14px; padding:20px 24px; }
  .doc-card       { display:flex; align-items:center; gap:12px; padding:14px; border:1px solid #e4eaf2; border-radius:10px; cursor:pointer; transition:all .15s; position:relative; }
  .doc-card:hover { background:#f4f8ff; border-color:#c5d6f5; }
  .doc-card-icon  { width:40px; height:40px; border-radius:10px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
  .doc-card-body  { flex:1; min-width:0; }
  .doc-card-name  { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 3px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .doc-card-type  { font-size:11.5px; color:#7a90b0; margin:0; }
  .doc-card-delete { width:26px; height:26px; border-radius:6px; border:1px solid #fecaca; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; font-size:11px; cursor:pointer; transition:all .15s; flex-shrink:0; }
  .doc-card-delete:hover { background:#e03535; color:#fff; }
  .doc-no-preview { display:flex; flex-direction:column; align-items:center; }
  
  /* ── Fields ──────────────────────────────────────────────────────────── */
  .fields-grid       { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
  .field-group       { display:flex; flex-direction:column; gap:5px; }
  .field-group--full { grid-column:1/-1; }
  .field-label       { font-size:13px; font-weight:600; color:#1a2b45; }
  .req               { color:#e03535; }
  .field-input       { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:9px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-input:disabled { background:#f4f7fb; color:#94a3b8; cursor:not-allowed; }
  .field-select      { appearance:auto; cursor:pointer; }
  textarea.field-input { resize:vertical; }
  .req               { color:#e03535; }

  /* ── Location checklist ── */
  .loc-checklist        { border:1.5px solid #dde5f0; border-radius:9px; max-height:160px; overflow-y:auto; padding:4px 0; }
  .loc-checklist-item   { display:flex; align-items:center; gap:10px; padding:8px 14px; cursor:pointer; font-size:13.5px; color:#1a2b45; transition:background .12s; }
  .loc-checklist-item:hover { background:#f4f7fb; }
  .loc-checklist-item input[type="checkbox"] { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
  .loc-checklist-empty  { padding:10px 14px; font-size:13px; color:#94a3b8; }

  /* ── Doc file picker ─────────────────────────────────────────────────── */
  .doc-file-btn { display:inline-flex; align-items:center; gap:6px; padding:9px 12px; border:1.5px dashed #dde5f0; border-radius:9px; font-size:13px; color:#7a90b0; background:#fff; cursor:pointer; width:100%; }
  .doc-file-btn:hover { border-color:#004AAD; color:#004AAD; }
  .doc-file-btn--chosen { border-style:solid; border-color:#004AAD; color:#004AAD; }
  
  /* ── Avatar upload ───────────────────────────────────────────────────── */
  .avatar-upload-zone { border:2px dashed #dde5f0; border-radius:12px; overflow:hidden; cursor:pointer; min-height:200px; display:flex; align-items:center; justify-content:center; transition:border-color .15s; }
  .avatar-upload-zone:hover { border-color:#004AAD; }
  .avatar-preview-img { width:100%; max-height:280px; object-fit:cover; }
  .avatar-upload-placeholder { display:flex; flex-direction:column; align-items:center; gap:8px; padding:32px; }
  .avatar-upload-icon { font-size:36px; color:#c0c9d8; }
  .upload-hint { font-size:12px; color:#94a3b8; margin:0; }
  
  /* ── Modals ──────────────────────────────────────────────────────────── */
  .modal-overlay      { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); opacity:0; pointer-events:none; transition:opacity .2s; }
  .modal-overlay.open { opacity:1; pointer-events:all; }
  .modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
  .modal-box     { background:#fff; border-radius:14px; width:100%; max-width:480px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; max-height:90vh; display:flex; flex-direction:column; }
  .modal-box--lg { max-width:720px; }
  .modal-box--sm { max-width:400px; }
  .modal-overlay.open .modal-box { transform:translateY(0); }
  .modal-box > form { display:flex; flex-direction:column; flex:1; min-height:0; overflow:hidden; }
  .modal-hd  { padding:18px 24px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
  .modal-ttl { font-size:15px; font-weight:700; color:#1a2b45; }
  .modal-close-btn { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
  .modal-close-btn:hover { background:#f4f7fb; }
  .modal-bd  { padding:20px 24px; display:flex; flex-direction:column; gap:14px; overflow-y:auto; flex:1; overscroll-behavior:contain; }
  .modal-ft  { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; flex-shrink:0; }
  
  /* ── Confirm modal ───────────────────────────────────────────────────── */
  .modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
  .modal-icon      { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; }
  .modal-icon--red { background:rgba(224,53,53,.1); color:#e03535; }
  .modal-body-clean { padding:16px 28px 8px; text-align:center; }
  .modal-heading    { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
  .modal-subtext    { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
  .modal-actions    { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
  .modal-btn        { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; flex:1; transition:background .15s; font-family:inherit; }
  .modal-btn--cancel { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; }
  .modal-btn--danger { background:#e03535; color:#fff; }
  .modal-btn--danger:hover:not(:disabled) { background:#c02828; }
  .modal-btn:disabled { opacity:.6; cursor:not-allowed; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:900px)  { .detail-grid { grid-template-columns:1fr 1fr; } }
  @media (max-width:600px)  {
    .detail-grid  { grid-template-columns:1fr; }
    .detail-item--full { grid-column:1; }
    .fields-grid  { grid-template-columns:1fr; }
    .field-group--full { grid-column:1; }
    .doc-grid     { grid-template-columns:1fr; }
  }

  /* ── Supervisor search-select ── */
  .ss-wrap        { position:relative; }
  .ss-input       { width:100%; padding-right:28px; }
  .ss-input--open { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); border-bottom-left-radius:0; border-bottom-right-radius:0; }
  .ss-clear       { position:absolute; right:10px; top:50%; transform:translateY(-50%); font-size:16px; color:#aab8cc; cursor:pointer; line-height:1; user-select:none; }
  .ss-clear:hover { color:#e03535; }
  .ss-drop        { position:absolute; top:100%; left:0; right:0; background:#fff; border:1.5px solid #004AAD; border-top:none; border-bottom-left-radius:8px; border-bottom-right-radius:8px; max-height:220px; overflow-y:auto; z-index:300; box-shadow:0 8px 24px rgba(0,0,0,.1); }
  .ss-option      { padding:9px 13px; font-size:13px; color:#1a2b45; cursor:pointer; display:flex; align-items:center; justify-content:space-between; gap:8px; }
  .ss-option:hover, .ss-option--active { background:#eef4ff; }
  .ss-emp-no      { font-size:11px; color:#7a90b0; }
  .ss-empty       { padding:10px 13px; font-size:13px; color:#7a90b0; }
  </style>